/**
 * Tron WalletConnect（@tronweb3/walletconnect-tron）
 * 与 EVM 的 ethereum-provider 分离：tron namespace + signTransaction
 */

import { db } from "../utils.mjs";
import { isWalletUserCancel } from "../walletCancel.mjs";
import { ensureMathBigIntSafe } from "../patchMathBigInt.mjs";
import { buildAppKitThemeOptions } from "./wcTheme.mjs";
import { startAppKitUiEnhancements, stopAppKitUiEnhancements } from "../appkitUiEnhance.mjs";
import { cleanupAppKitDom } from "../appkitCleanup.mjs";
import { base58de, base58en } from "./tron.mjs";
import { TRON_CHAIN_ID } from "./tronInject.mjs";

import { TRON_WC_WALLET_TYPE } from "../tronWcDetect.mjs";
export { TRON_WC_WALLET_TYPE };

const PROJECT_ID = "fbdb8e9cdd25c6f9e115acca94322b3c";
const DEFAULT_RELAY = "wss://relay.walletconnect.com";

/** TokenPocket / TronLink 等支持 Tron WC 的钱包 */
const TRON_WC_FEATURED_WALLET_IDS = [
  "4622a2b2d6af1c9844944291e5e7351a6aa24cd7b23099efac1b2fd875da31a0",
  "1ae92b26df02f0abca6304df07debccd18262fdf5fe82daa81593582dac9a369",
];

let activeWallet = null;
let activeProvider = null;
let listenerUnsubs = [];

export function isTronWalletConnectProvider(provider) {
  return Boolean(provider && provider.isTronWalletConnect === true);
}

function normalizeTronAccountHex(address) {
  const raw = String(address || "").trim();
  if (!raw) return "";
  if (raw.startsWith("T")) {
    try {
      return base58de(raw);
    } catch (_e) {
      return "";
    }
  }
  return raw.startsWith("0x") ? raw : `0x${raw}`;
}

function wrapTronWalletProvider(wallet, addressBase58) {
  const b58 = String(addressBase58 || "").trim();
  const hex = normalizeTronAccountHex(b58);
  const provider = {
    isWalletConnect: true,
    isTronWalletConnect: true,
    chainId: TRON_CHAIN_ID,
    tronAddressBase58: b58,
    _tronWallet: wallet,
    disconnect: () => wallet.disconnect(),
    signMessage: (message) => wallet.signMessage(message),
    signTransaction: (transaction) => wallet.signTransaction(transaction),
  };
  return { provider, account: hex, chainId: TRON_CHAIN_ID };
}

async function loadTronWalletClass() {
  ensureMathBigIntSafe();
  const mod = await import("@tronweb3/walletconnect-tron");
  return mod;
}

function resolveWalletConnectMetadataUrl() {
  if (typeof window === "undefined") return "https://hieswap.com";
  const origin = window.location.origin;
  if (/^https?:\/\/(localhost|127\.0\.0\.1|\d{1,3}(?:\.\d{1,3}){3})(:\d+)?$/i.test(origin)) {
    return "https://hieswap.com";
  }
  return origin;
}

function buildWalletConfig(options = {}) {
  const origin = resolveWalletConnectMetadataUrl();
  const appKitTheme = buildAppKitThemeOptions(options.themeMode);
  return {
    network: undefined,
    options: {
      relayUrl: DEFAULT_RELAY,
      projectId: PROJECT_ID,
      metadata: {
        name: "HieSwap Bridge",
        description: "Cross-chain Bridge",
        url: origin,
        icons: ["https://hieswap.com/favicon.ico"],
      },
    },
    allWallets: "HIDE",
    featuredWalletIds: TRON_WC_FEATURED_WALLET_IDS,
    themeMode: appKitTheme.themeMode,
    themeVariables: appKitTheme.themeVariables,
  };
}

async function createTronWallet(options = {}) {
  await import("./walletconnect.mjs").then((m) => m.cancelWalletConnectPairing()).catch(() => {});
  const { WalletConnectWallet, WalletConnectChainID } = await loadTronWalletClass();
  const cfg = buildWalletConfig(options);
  cfg.network = WalletConnectChainID.Mainnet;
  return new WalletConnectWallet(cfg);
}

export async function disposeActiveTronWalletConnect() {
  for (const off of listenerUnsubs) {
    try {
      off();
    } catch (_e) {
      void _e;
    }
  }
  listenerUnsubs = [];
  stopAppKitUiEnhancements();
  const w = activeWallet;
  activeWallet = null;
  activeProvider = null;
  if (!w) {
    cleanupAppKitDom();
    return;
  }
  try {
    await w.disconnect();
  } catch (_e) {
    void _e;
  }
  cleanupAppKitDom();
}

function persistTronWcTopic(wallet) {
  try {
    const topic = wallet?.client?.session?.topic || wallet?._session?.topic;
    if (topic) db.set("topic", topic);
  } catch (_e) {
    void _e;
  }
}

function attachTronWcListeners(wallet, callbacks = {}) {
  for (const off of listenerUnsubs) {
    try {
      off();
    } catch (_e) {
      void _e;
    }
  }
  listenerUnsubs = [];
  if (!wallet || typeof wallet.on !== "function") return;

  const onAccounts = wallet.on("accountsChanged", (accounts) => {
    const b58 = Array.isArray(accounts) ? accounts[0] : "";
    callbacks.onAccountsChanged?.(b58);
  });
  if (typeof onAccounts === "function") listenerUnsubs.push(onAccounts);

  const onDisconnect = wallet.on("disconnect", () => {
    callbacks.onDisconnect?.();
  });
  if (typeof onDisconnect === "function") listenerUnsubs.push(onDisconnect);
}

export function bindTronWalletConnectListeners(callbacks = {}) {
  if (!activeWallet) return;
  attachTronWcListeners(activeWallet, callbacks);
}

async function finalizeTronConnect(wallet, addressBase58) {
  const wrapped = wrapTronWalletProvider(wallet, addressBase58);
  activeWallet = wallet;
  activeProvider = wrapped.provider;
  persistTronWcTopic(wallet);
  return wrapped;
}

export async function initTronWalletConnect(options = {}) {
  ensureMathBigIntSafe();
  await disposeActiveTronWalletConnect();
  const wallet = await createTronWallet(options);
  startAppKitUiEnhancements();
  try {
    const { address } = await wallet.connect();
    if (!address) {
      await disposeActiveTronWalletConnect();
      throw new Error("NO_TRON_WC_ACCOUNT");
    }
    return finalizeTronConnect(wallet, address);
  } catch (err) {
    stopAppKitUiEnhancements();
    throw err;
  }
}

export async function beginTronWalletConnectPairing(options = {}) {
  await disposeActiveTronWalletConnect();
  const wallet = await createTronWallet(options);
  let connectResolve;
  let connectReject;
  const connectPromise = new Promise((resolve, reject) => {
    connectResolve = resolve;
    connectReject = reject;
  });

  wallet
    .connect({
      onUri: options.onDisplayUri,
    })
    .then(({ address }) => {
      if (!address) {
        connectReject(new Error("NO_TRON_WC_ACCOUNT"));
        return;
      }
      finalizeTronConnect(wallet, address).then(connectResolve).catch(connectReject);
    })
    .catch(async (err) => {
      await disposeActiveTronWalletConnect();
      connectReject(err);
    });

  return { wallet, connectPromise };
}

export async function cancelTronWalletConnectPairing() {
  await disposeActiveTronWalletConnect();
}

export async function tryRestoreTronWalletConnect() {
  const wallet = await createTronWallet({});
  const { address } = await wallet.checkConnectStatus();
  if (!address) {
    return null;
  }
  return finalizeTronConnect(wallet, address);
}

export const getAccountWalletConnectTron = async (options = {}) => {
  const prevType = db.get("wallet_type", "inject");
  db.set("wallet_type", TRON_WC_WALLET_TYPE);
  try {
    return await initTronWalletConnect(options);
  } catch (err) {
    db.set("wallet_type", isWalletUserCancel(err) ? prevType : "inject");
    throw err;
  }
};

export function getActiveTronWalletConnectProvider() {
  return activeProvider;
}

export function tronWcAddressBase58(provider) {
  if (!isTronWalletConnectProvider(provider)) return "";
  return String(provider.tronAddressBase58 || "").trim();
}

export function tronWcAccountHex(provider) {
  const b58 = tronWcAddressBase58(provider);
  return b58 ? normalizeTronAccountHex(b58) : "";
}

/** 供 UI 展示 T 地址 */
export function tronWcDisplayAddress(provider, accountHex) {
  const b58 = tronWcAddressBase58(provider);
  if (b58) return b58;
  const hex = String(accountHex || "").trim();
  if (!hex) return "";
  try {
    return base58en(hex);
  } catch (_e) {
    return hex;
  }
}
